import React, { useEffect, useRef, useState } from "react";
import uamServices from "@tvg/api/uam";
import { string } from "yup";
import { includes } from "lodash";
import tvgConfig from "@tvg/conf";
import { useSelector, useDispatch } from "react-redux";

import { openExternalLink } from "@tvg/utils/mobileUtils";
import ModalV2 from "@tvg/atomic-ui/_templates/ModalV2";
import RegistrationPromoCodeTemplate from "@tvg/atomic-ui/_templates/RegistrationPromoCode";
import {
  handlePromoCodeModalRegistrationAction,
  setAPromotionRegistrationAction,
  setSelectedPromotionRegistrationAction,
  setInvalidPromoRegistrationAction
} from "../actions";
import { getPromosInfo, handleInputOnBlurGtm } from "../utils/handlers";
import {
  onApplyPromoCodeGtmEvent,
  onPromoCodeAppliedGtmEvent,
  onPromoCodeEditedGtmEvent,
  onPromoCodeRemovedGtmEvent,
  onPromoCodeModalCloseGtmEvent
} from "../utils/gtm/promoCode";
import {
  getInvalidPromo,
  getIsPromoCodeModalOpen,
  getPromoCodesMap,
  getRegistrationAllPromosWithTC,
  getRegistrationTexts,
  getStoredPromoCodes,
  getStoredSelectedPromoCode
} from "../utils/selectors";

export const closeModal = (dispatch) => {
  dispatch(handlePromoCodeModalRegistrationAction(false));
  onPromoCodeModalCloseGtmEvent();
};

export const handleTCClick = (TCLink) => {
  const httpRegex =
    // eslint-disable-next-line
    /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi;

  if (httpRegex.test(TCLink)) {
    if (
      window?.handleNativeMessages &&
      includes(["iosnative", "androidnative"], tvgConfig().product)
    ) {
      window.handleNativeMessages("OPEN_EXTERNAL_URL", {
        url: TCLink
      });
    } else {
      openExternalLink(TCLink);
    }
  }
};

export const PromoCodeModalComponent = ({ device }) => {
  const dispatch = useDispatch();

  const isPromoCodeModalOpen = useSelector(getIsPromoCodeModalOpen);
  const registrationTexts = useSelector(getRegistrationTexts);
  const promoCodesMap = useSelector(getPromoCodesMap);
  const storedPromoCodes = useSelector(getStoredPromoCodes);
  const storedSelectedPromoCode = useSelector(getStoredSelectedPromoCode);
  const invalidPromo = useSelector(getInvalidPromo);
  const registrationAllPromosWithTC = useSelector(
    getRegistrationAllPromosWithTC
  );

  const formRef = useRef(null);
  const [showApplyButton, setShowApplyButton] = useState(false);
  const [verifyPromoCode, setVerifyPromoCode] = useState(false);
  const [isInputDirty, setIsInputDirty] = useState(false);

  useEffect(() => {
    if (formRef.current) {
      if (isInputDirty) {
        formRef.current.setFieldError(
          "promoCodeInput",
          registrationTexts.modals.promoCode.error
        );
      } else {
        formRef.current.setFieldError("promoCodeInput", "");
      }
    }
  }, [isInputDirty]);

  const [selectedPromoCode, setSelectedPromoCode] = useState("");

  useEffect(() => {
    if (storedSelectedPromoCode !== "") {
      setSelectedPromoCode(storedSelectedPromoCode);
    }
  }, [storedSelectedPromoCode]);

  const handlePromoCodeChange = (value) => {
    const alphanumericValuesExp = /^[a-z0-9]+$/i;
    const isInputFilled = value.trim().length > 0;

    setIsInputDirty(!value.match(alphanumericValuesExp) && isInputFilled);
    setShowApplyButton(value.match(alphanumericValuesExp) && isInputFilled);
  };

  const handlePromoCodeClick = (promo) => {
    setSelectedPromoCode(selectedPromoCode === promo ? "" : promo);
  };

  const handleInputFocus = () => {
    setIsInputDirty(false);
  };

  const handleClickApplyButton = async () => {
    setShowApplyButton(false);
    setVerifyPromoCode(true);
    const code = formRef.current.getFieldValue("promoCodeInput").toUpperCase();
    onApplyPromoCodeGtmEvent("Review Your Information");

    try {
      const { data } = await uamServices.getPromoValidation(code);
      const { response } = data;
      const promoInfo = getPromosInfo({
        stringCode: code.toLowerCase(),
        promoResponse: response,
        message: promoCodesMap
      });

      setVerifyPromoCode(false);
      setSelectedPromoCode(code);
      dispatch(setAPromotionRegistrationAction(code, promoInfo));
      if (invalidPromo) {
        dispatch(setInvalidPromoRegistrationAction(false));
      }
      formRef.current.setFieldValue("promoCodeInput", "");
    } catch (err) {
      setIsInputDirty(true);
      setVerifyPromoCode(false);
    }
  };

  const handleSubmit = () => {
    if (
      selectedPromoCode &&
      storedSelectedPromoCode &&
      selectedPromoCode !== storedSelectedPromoCode
    ) {
      onPromoCodeEditedGtmEvent(
        `Manual - ${selectedPromoCode}`,
        "Review Your Information",
        selectedPromoCode
      );
    } else if (selectedPromoCode) {
      onPromoCodeAppliedGtmEvent(
        `Manual - ${selectedPromoCode}`,
        "Review Your Information",
        selectedPromoCode
      );
    } else if (storedSelectedPromoCode) {
      onPromoCodeRemovedGtmEvent(
        `Manual - ${storedSelectedPromoCode}`,
        "Review Your Information",
        storedSelectedPromoCode
      );
    }
    dispatch(setSelectedPromotionRegistrationAction(selectedPromoCode));
    dispatch(handlePromoCodeModalRegistrationAction(false));
  };

  const handleClose = () => {
    closeModal(dispatch);
    setSelectedPromoCode(storedSelectedPromoCode);
  };

  const isMobile = device === "mobile";

  return (
    <ModalV2
      title={registrationTexts.modals.promoCode.modalTitle}
      isOpen={isPromoCodeModalOpen}
      onClose={handleClose}
      onOverlayClick={handleClose}
      titleType="info"
      qaLabel="modal-promo-code"
      animation={isMobile ? "bottom" : "bottomFloating"}
      offsetLeft={isMobile ? 0 : 12}
      offsetRight={isMobile ? 0 : 12}
      fixedWidth={isMobile ? "100%" : "375px"}
      isFullWidth={isMobile}
      offsetTop={66}
    >
      {() => (
        <RegistrationPromoCodeTemplate
          formRef={formRef}
          firstTitle={registrationTexts.modals.promoCode.firstTitle}
          secondTitle={registrationTexts.modals.promoCode.secondTitle}
          buttonText={registrationTexts.modals.promoCode.buttonText}
          showApplyButton={showApplyButton}
          showInputLoading={verifyPromoCode}
          isButtonDisabled={!Object.keys(storedPromoCodes).length}
          handlePromoCodeOnBlur={handleInputOnBlurGtm(
            "promoCodeInput",
            string().min(1),
            false,
            registrationTexts.modals.promoCode.modalTitle
          )}
          handlePromoCodeChange={handlePromoCodeChange}
          onClickApplyButton={handleClickApplyButton}
          handleSubmit={handleSubmit}
          onInputFocus={handleInputFocus}
          promoCodes={storedPromoCodes}
          selectedPromoCode={selectedPromoCode}
          handlePromoCodeClick={handlePromoCodeClick}
          registrationAllPromosWithTC={registrationAllPromosWithTC}
          handleTCClick={handleTCClick}
        />
      )}
    </ModalV2>
  );
};

export default PromoCodeModalComponent;
